import { state } from '@codesuma/baseline'
import { toJalaali, toGregorian, jalaaliMonthLength } from './jalaali'
import { toHijri } from './hijri'
import { CalendarSystem } from './settings'
import { J_MONTHS, G_MONTHS } from '../configs/names'

// A month in the active primary system (Jalaali or Gregorian).
export interface IMonthInfo {
    year: number
    month: number // 1-based in the active system
    name: string // Persian month name
    days: number
}

// One grid cell: the same day expressed in all three calendars.
export interface IDayCell {
    jy: number
    jm: number
    jd: number
    gy: number
    gm: number
    gd: number
    hy: number
    hm: number
    hd: number
    inMonth: boolean // belongs to the displayed month (vs. gray lead-in/out)
    isToday: boolean
}

// The month visible at slider index 0 is today's month, so all slider
// navigation is month arithmetic relative to today. These three move together
// when the day turns — see init below — and nothing else in the app reads the
// clock, so this is the one place a new day enters.
let now = new Date()
let anchor = toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate())
let anchorWeekday = persianWeekday(now.getDay())

// JS getDay() is Sunday-first (0 = Sunday); the Persian week is Saturday-first.
function persianWeekday(getDay: number) {
    return (getDay + 1) % 7 // 0 = Saturday … 6 = Friday
}

function monthAt(index: number, system: CalendarSystem): IMonthInfo {
    if (system === 'gregorian') {
        const months = now.getFullYear() * 12 + now.getMonth() + index
        const gy = Math.floor(months / 12)
        const gm = (months % 12) + 1
        return { year: gy, month: gm, name: G_MONTHS[gm - 1], days: new Date(gy, gm, 0).getDate() }
    }
    const months = anchor.jy * 12 + (anchor.jm - 1) + index
    const jy = Math.floor(months / 12)
    const jm = (months % 12) + 1
    return { year: jy, month: jm, name: J_MONTHS[jm - 1], days: jalaaliMonthLength(jy, jm) }
}

// The slider index a given month sits at — the inverse of monthAt. The year
// page builds months from a year rather than from an offset, and a tap on one
// of its cards hands the index straight back to the home slider.
function monthIndex(year: number, month: number, system: CalendarSystem) {
    return system === 'gregorian'
        ? year * 12 + (month - 1) - (now.getFullYear() * 12 + now.getMonth())
        : year * 12 + (month - 1) - (anchor.jy * 12 + (anchor.jm - 1))
}

function today() {
    return {
        ...anchor,
        wd: anchorWeekday,
        gy: now.getFullYear(),
        gm: now.getMonth() + 1,
        gd: now.getDate(),
    }
}

// The day `offset` days after the given Gregorian date, expressed in all
// three calendars plus its Persian weekday — the day page's slide data.
function dayAt(gy: number, gm: number, gd: number, offset: number): IDayCell & { wd: number } {
    const date = new Date(gy, gm - 1, gd + offset)
    const y = date.getFullYear()
    const m = date.getMonth() + 1
    const d = date.getDate()
    const j = toJalaali(y, m, d)
    const h = toHijri(y, m, d)
    const isToday = y === now.getFullYear() && m === now.getMonth() + 1 && d === now.getDate()
    return { ...j, gy: y, gm: m, gd: d, ...h, inMonth: true, isToday, wd: persianWeekday(date.getDay()) }
}

// Always 6 weeks (42 cells): the month itself plus lead-in/lead-out days of
// the adjacent months, so every slide has the same grid height. weekStart is
// a Persian weekday index (0 = Saturday).
function grid(month: IMonthInfo, system: CalendarSystem, weekStart: number): IDayCell[] {
    const first = system === 'gregorian'
        ? { gy: month.year, gm: month.month, gd: 1 }
        : toGregorian(month.year, month.month, 1)
    const firstDate = new Date(first.gy, first.gm - 1, first.gd)
    const offset = (persianWeekday(firstDate.getDay()) - weekStart + 7) % 7

    const cells: IDayCell[] = []
    for (let i = 0; i < 42; i++) {
        const date = new Date(first.gy, first.gm - 1, first.gd - offset + i)
        const gy = date.getFullYear()
        const gm = date.getMonth() + 1
        const gd = date.getDate()
        const j = toJalaali(gy, gm, gd)
        const h = toHijri(gy, gm, gd)
        const inMonth = system === 'gregorian'
            ? gy === month.year && gm === month.month
            : j.jy === month.year && j.jm === month.month
        const isToday = gy === now.getFullYear() && gm === now.getMonth() + 1 && gd === now.getDate()
        cells.push({ ...j, gy, gm, gd, ...h, inMonth, isToday })
    }
    return cells
}

// --- The day turning ---
//
// Everything above is worked out from `now`, so a day that turns while the app
// is open has to be noticed here and announced: state.on('today', handler) is
// how the pages hear about it, the same way they hear about a moon-sighting
// correction.
//
// Two things watch for it, and both are needed. The timer covers an app that is
// open and awake. The wake-up checks cover a phone, where a backgrounded or
// sleeping WebView has its timers throttled or suspended and the midnight one
// cannot be relied on to fire on the minute — or at all. Neither is trusted to
// be right: both call the same check, which compares the calendar date and does
// nothing at all unless it has actually changed.

const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

let timer = 0

// One second past the next local midnight — past it, rather than on it, so the
// clock has certainly turned over by the time the check reads it. Built from
// the date parts rather than by adding hours, so it lands on midnight through a
// daylight-saving change too.
function arm(from: Date) {
    clearTimeout(timer)
    const next = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1, 0, 0, 1)
    timer = window.setTimeout(check, next.getTime() - from.getTime())
}

function check() {
    const current = new Date()
    // Re-armed on every check, not only on the ones that find a new day: a
    // check that runs early leaves the timer pointing at the same midnight.
    arm(current)
    if (sameDay(current, now)) return
    now = current
    anchor = toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate())
    anchorWeekday = persianWeekday(now.getDay())
    state.set('today', today())
}

function init() {
    arm(new Date())
    // Coming back to the app: the tab is looked at again, the window is focused,
    // or the native app returns from the background. Any of them can be the
    // first moment the app learns that hours have passed.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check()
    })
    window.addEventListener('focus', check)
    // Reached through window.Capacitor rather than imported, the way the
    // reminders service reaches LocalNotifications — the plugin belongs to the
    // shell, not to this bundle.
    const app = (window as any).Capacitor?.Plugins?.App
    app?.addListener?.('appStateChange', ({ isActive }: { isActive: boolean }) => {
        if (isActive) check()
    })
}

export default {
    monthAt,
    monthIndex,
    today,
    grid,
    dayAt,
    init,
}
